import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useUploadJobStore, type UploadJob } from '@/stores/uploadJobStore';
import { reviewPathFor } from '@/utils/reviewPath';
import { UploadProgressCard } from './UploadProgressCard';
import { ConfirmSheet } from '@/components/ui/ConfirmSheet';

interface SidebarUploadPanelProps {
  isExpanded: boolean;
  isMobile: boolean;
}

const isInFlight = (phase: UploadJob['phase']) =>
  phase === 'creating' || phase === 'uploading' || phase === 'transcribing';

/**
 * Upload-progress cards docked at the bottom of the clinical sidebar, above the
 * user chip. Renders straight from the store so progress follows the clinician
 * across clinical routes. Full cards when the sidebar is expanded; a compact
 * activity icon when it is collapsed to the rail. Cancel asks for confirmation
 * first, since it stops an in-progress upload/transcription.
 */
export function SidebarUploadPanel({ isExpanded, isMobile }: SidebarUploadPanelProps) {
  const jobs = useUploadJobStore((s) => s.jobs);
  const navigate = useNavigate();
  const [pendingCancel, setPendingCancel] = useState<UploadJob | null>(null);

  const active = Object.values(jobs).filter((job) => job.phase !== 'cancelled');
  if (active.length === 0) return null;

  const confirmCancel = () => {
    if (pendingCancel) {
      // pendingCancel is a snapshot; its key can go stale if the job re-keyed from its temp id
      // to the server recordingId while the dialog was open. Resolve the current live job by the
      // stable storageId and cancel that.
      const { jobs, cancel } = useUploadJobStore.getState();
      const live =
        (pendingCancel.storageId
          ? Object.values(jobs).find((j) => j.storageId === pendingCancel.storageId)
          : undefined) ?? jobs[pendingCancel.key];
      if (live) cancel(live.key);
    }
    setPendingCancel(null);
  };

  const review = (job: UploadJob) => {
    navigate(reviewPathFor(job));
    useUploadJobStore.getState().dismiss(job.key);
  };

  const retry = (job: UploadJob) => {
    useUploadJobStore.getState().dismiss(job.key);
    void import('@/services/uploadRetryWorker')
      .then(async ({ uploadRetryWorker }) => {
        const count = await uploadRetryWorker.retryAllFailed();
        if (count === 0) {
          toast('No saved recording to retry.', { icon: 'ℹ️' });
        }
      })
      .catch(() => toast.error('Could not start retry. Please try again.'));
  };

  // Name anchors the caption; date and time group beneath as one "when" unit, matching the
  // header clock. Falls back to date-only, then name-only, then a generic label.
  const when = pendingCancel
    ? [pendingCancel.encounterDate, pendingCancel.encounterTime].filter(Boolean).join(' · ')
    : '';

  const confirmModal = (
    <ConfirmSheet
      open={!!pendingCancel}
      onCancel={() => setPendingCancel(null)}
      onConfirm={confirmCancel}
      title="Stop this upload?"
      description="Uploading and transcribing will stop. You can record this visit again later."
      confirmLabel="Stop upload"
      cancelLabel="Keep uploading"
    >
      {pendingCancel && (
        <div className="flex flex-col items-center gap-1">
          <span className="font-semibold text-clinical-navy">{pendingCancel.patientName || 'This recording'}</span>
          {when && (
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {when}
            </span>
          )}
        </div>
      )}
    </ConfirmSheet>
  );

  // Collapsed rail: a 240px card cannot fit 64px, so surface a single aggregate icon.
  if (!isExpanded && !isMobile) {
    const anyInFlight = active.some((job) => isInFlight(job.phase));
    const anyFailed = active.some((job) => job.phase === 'failed');
    const Icon = anyInFlight ? ArrowPathIcon : anyFailed ? ExclamationTriangleIcon : CheckCircleIcon;
    const color = anyInFlight ? 'text-blue-500' : anyFailed ? 'text-red-500' : 'text-green-500';
    return (
      <>
        {confirmModal}
        <div className="flex justify-center border-t border-gray-100 py-3" role="status" aria-label="Upload activity">
          <Icon className={`h-5 w-5 ${anyInFlight ? 'animate-spin ' : ''}${color}`} aria-hidden="true" />
        </div>
      </>
    );
  }

  return (
    <>
      {confirmModal}
      <div className="flex max-h-[40vh] shrink-0 flex-col gap-2 overflow-y-auto border-t border-gray-100 p-2">
        <AnimatePresence initial={false}>
          {active.map((job) => (
            <UploadProgressCard
              key={job.storageId ?? job.key}
              job={job}
              onCancel={() => setPendingCancel(job)}
              onRetry={() => retry(job)}
              onReview={() => review(job)}
            />
          ))}
        </AnimatePresence>
      </div>
    </>
  );
}
